// services/tour.service.js
const anchorMap = new Map();

let currentStepIndex = 0;
let totalSteps = 0;
let listeners = [];
let completionListeners = [];
let shepherdInstance = null;

export const TourService = {
    register(key, el) {
        anchorMap.set(key, el);
    },

    registerAll(vm, keys = []) {
        vm.$nextTick(() => {
            keys.forEach((key) => {
                const ref = vm.$refs[key];
                if (ref) {
                    this.register(key, ref);
                } else {
                    console.warn(`Tour ref "${key}" not found on component.`);
                }
            });
        });
    },

    get(key) {
        return anchorMap.get(key);
    },

    reset() {
        //anchorMap.clear();
        currentStepIndex = 0;
        totalSteps = 0;
        //listeners = [];
        shepherdInstance = null;
    },

    onComplete(callback) {
        completionListeners.push(callback);
    },


    onStepChange(callback) {
        listeners.push(callback);
    },

    notifyStepChange() {
        const step = shepherdInstance?.getCurrentStep?.();
        listeners.forEach((cb) => cb(currentStepIndex, totalSteps, step));
    },


    showStep(index) {
        currentStepIndex = index;
        this.notifyStepChange();
    },

    startTour(VueInstance, stepsBuilder) {
        if (shepherdInstance) return;

        shepherdInstance = VueInstance.$shepherd({
            defaultStepOptions: {
                scrollTo: true,
                popperOptions: {
                    modifiers: [
                        {
                            name: 'offset',
                            options: { offset: [0, 0] }
                        }
                    ]
                }
            }
        });

        const steps = stepsBuilder();
        shepherdInstance.addSteps(steps);
        totalSteps = steps.length;

        let stepCounter = 0; // Manual step counter

        shepherdInstance.on('show', () => {
            currentStepIndex = stepCounter;
            TourService.notifyStepChange();
        });

        shepherdInstance.on('complete', () => {
            stepCounter = 0;
            completionListeners.forEach(cb => cb());
            this.reset()
        });

        shepherdInstance.on('cancel', () => {
            stepCounter = 0;
            completionListeners.forEach(cb => cb());
        });

        // Wrap original next to increment stepCounter
        const originalNext = shepherdInstance.next.bind(shepherdInstance);
        shepherdInstance.next = () => {
            stepCounter++;
            originalNext();
        };

        // Also patch back for `back()`
        const originalBack = shepherdInstance.back.bind(shepherdInstance);
        shepherdInstance.back = () => {
            stepCounter = Math.max(0, stepCounter - 1);
            originalBack();
        };

        shepherdInstance.start();
    },



    buildNavbarSteps(isAgencyAdmin) {
        const steps = [
            {
                id: 'step-expand-dashboard',
                attachTo: { element: this.get('dashboardExpand').getAnchor(), on: 'right-start' },
                title: 'Dashboard',
                text: 'Click the Dashboard header to expand.',
                advanceOn: {
                    selector: '[data-tour-id="dashboardExpand"]',
                    event: 'click'
                }
            },
            {
                id: 'step-dashboard-overview',
                attachTo: { element: this.get('dashboardOverview').getAnchor(), on: 'right-start' },
                title: 'Dashboard: Overview',
                text: 'This page gives you a high-level summary of your metrics and current proposals.',
                buttons: [{ text: 'Next', action: () => { shepherdInstance.next(); } }]
            },
            {
                id: 'step-expand-proposals',
                attachTo: { element: this.get('proposalsExpand').getAnchor(), on: 'right-start' },
                title: 'Proposals',
                text: 'Click the Proposals header to expand.',
                advanceOn: {
                    selector: '[data-tour-id="proposalsExpand"]',
                    event: 'click'
                }
            },
            {
                id: 'step-new-proposal',
                attachTo: { element: this.get('newProposal').getAnchor(), on: 'right-start' },
                title: 'Proposals: New',
                text: 'This is where you can start a new proposal.',
                buttons: [{ text: 'Next', action: () => { shepherdInstance.next(); } }]
            },
            {
                id: 'step-all-proposals',
                attachTo: { element: this.get('allProposals').getAnchor(), on: 'right-start' },
                title: 'Proposals: All',
                text: 'This is where all of your proposals live, including the archived or closed accounts...',
                buttons: [{ text: 'Next', action: () => { shepherdInstance.next(); } }]
            },
            {
                id: 'step-expand-goals',
                attachTo: { element: this.get('goalsExpand').getAnchor(), on: 'right-start' },
                title: 'Goals',
                text: 'Click the Goals header to expand.',
                advanceOn: {
                    selector: '[data-tour-id="goalsExpand"]',
                    event: 'click'
                }
            },
            {
                id: 'step-annual-plan',
                attachTo: {
                    element: isAgencyAdmin
                        ? this.get('agencyAdminPlan')?.getAnchor?.()
                        : this.get('producerAdminPlan')?.getAnchor?.(),
                    on: 'right-start'
                },
                title: 'Goals: Annual Plan',
                text: isAgencyAdmin
                    ? 'As an Agency Admin you can review the Agency Annual Plan...'
                    : 'This is your Producer Annual Plan...',
                buttons: [{ text: 'Next', action: () => { shepherdInstance.next(); } }]
            },
            {
                id: 'step-expand-email-marketing',
                attachTo: { element: this.get('emailMarketingExpand').getAnchor(), on: 'right-start' },
                title: 'Email Marketing',
                text: 'Click the Email Marketing header to expand.',
                advanceOn: {
                    selector: '[data-tour-id="emailMarketingExpand"]',
                    event: 'click'
                }
            },
            {
                id: 'nine-bullets',
                attachTo: { element: this.get('nineBullets').getAnchor(), on: 'right-start' },
                title: 'Email Marketing: 9 Bullets to Bind-it',
                text: 'Our campaign automation feature leveraging decision maker psychology...',
                buttons: [{ text: 'Next', action: () => { shepherdInstance.next(); } }]
            },
            {
                id: 'document-library',
                attachTo: { element: this.get('documentLibrary').getAnchor(), on: 'right-start' },
                title: 'Email Marketing: Document Library',
                text: 'Upload your documents to use with proposals or 9 Bullets.',
                buttons: [{ text: 'Finish', action: () => { shepherdInstance.complete(); } }]
            }
        ];

        totalSteps = steps.length;
        return steps;
    },
    getCurrentStepIndex() {
        return currentStepIndex; // Convert from 0-based to 1-based
    },

    getTotalSteps() {
        return totalSteps; // Convert from 0-based to 1-based
    },

    isTourActive() {
        return shepherdInstance !== null;
    },
    next() {
        if (shepherdInstance && typeof shepherdInstance.next === 'function') {
            shepherdInstance.next();
        }
    },

    back() {
        if (shepherdInstance && typeof shepherdInstance.back === 'function') {
            shepherdInstance.back();
        }
    },

    complete() {
        if (shepherdInstance && typeof shepherdInstance.complete === 'function') {
            shepherdInstance.complete();
        }
    },

};
